import { injectable, injectFromBase } from 'inversify';
import CleengAccountService from '@jwp/ott-common/src/services/integrations/cleeng/CleengAccountService';
import type { UpdateFavorites, UpdateWatchHistory } from '@jwp/ott-common/types/account';
import type { UpdateCustomerResponse } from '@jwp/ott-common/src/services/integrations/cleeng/types/account';
import type { SerializedWatchHistoryItem } from '@jwp/ott-common/types/watchHistory';
import type { SerializedFavorite } from '@jwp/ott-common/types/favorite';

type DeprecatedWatchHistory<D> = {
  state: {
    watchHistory: D;
  };
};

type DeprecatedFavorites<D> = {
  state: {
    favorites: D;
  };
};

const serializeDeprecatedPersonalData = <T>(key: 'watchHistory' | 'favorites', data: T) => {
  return {
    state: {
      [key]: data,
    },
    version: '0',
  };
};

@injectable()
@injectFromBase({ extendConstructorArguments: true })
export default class SymphonyCleengAccountService extends CleengAccountService {
  updateWatchHistory: UpdateWatchHistory = async ({ user, history }) => {
    const payload = {
      id: user.id,
      externalData: { ...this.externalData, history: serializeDeprecatedPersonalData('watchHistory', history) },
    };
    const { errors, responseData } = await this.cleengService.patch<UpdateCustomerResponse>(`/customers/${user.id}`, JSON.stringify(payload), {
      authenticate: true,
      keepalive: true,
    });

    this.handleErrors(errors);
    this.externalData = responseData.externalData || {};
  };

  override updateFavorites: UpdateFavorites = async ({ user, favorites }) => {
    const payload = {
      id: user.id,
      externalData: { ...this.externalData, favorites: serializeDeprecatedPersonalData('favorites', favorites) },
    };
    const { errors, responseData } = await this.cleengService.patch<UpdateCustomerResponse>(`/customers/${user.id}`, JSON.stringify(payload), {
      authenticate: true,
      keepalive: true,
    });

    this.handleErrors(errors);
    this.externalData = responseData.externalData || {};
  };

  getWatchHistory = async () => {
    return ((this.externalData['history'] as DeprecatedWatchHistory<SerializedWatchHistoryItem[]>)?.state?.watchHistory || []) as SerializedWatchHistoryItem[];
  };

  getFavorites = async () => {
    return ((this.externalData['favorites'] as DeprecatedFavorites<SerializedFavorite[]>)?.state?.favorites || []) as SerializedFavorite[];
  };
}
